import { Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@environment';
import { HttpApi } from './http.api';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { ActivityQueryParams } from '@core/models/activity.models';
import { DEFAULT_QUERY_PARAMS } from '@wlcm/angular/core';
import { WlcmPaginatedData, WlcmQueryParams } from '@wlcm/common';
import { LiveClass } from '@core/models/entities/class.models';
import { EventQueryParams } from '@core/models/query-param.models';
import { BookingEvent, BookingEventPaginated } from '@core/models/entities/booking-event.models';
import { JoinEvent, StartMeetingResponse } from '@core/models/zoom.models';

@Injectable({
  providedIn: 'root',
})
export class BookingEventService {
  loading: WritableSignal<boolean> = signal(false);

  events: WritableSignal<BookingEvent[]> = signal([]);

  eventsForToday: WritableSignal<BookingEvent[]> = signal([]);

  pagination: WritableSignal<BookingEventPaginated | null> = signal(null);

  joinEvent: WritableSignal<JoinEvent | null> = signal(null);

  startEvent: WritableSignal<StartMeetingResponse | null> = signal(null);

  queryParams: WritableSignal<EventQueryParams> = signal({
    ...DEFAULT_QUERY_PARAMS,
  });

  lastDateRange: WritableSignal<ActivityQueryParams | null> = signal(null);

  lastDate: WritableSignal<Date | null> = signal(null);

  private readonly baseUrl: string = environment.apiUrl;

  constructor(private httpApi: HttpApi) {}

  getBookingEventForToday(isInstructor: boolean = false, queryParams?: ActivityQueryParams): Observable<void> {
    const path = isInstructor ? '' : '/available';

    const params = {
      ...DEFAULT_QUERY_PARAMS,
      limit: 10,
      ...(isInstructor ? queryParams : {}),
    };

    return this.httpApi
      .get<BookingEvent[] | BookingEventPaginated>(`${this.baseUrl}/booking-event${path}`, {
        params,
      })
      .pipe(
        map(res => {
          const events = isInstructor ? (res as BookingEvent[]) : ((res as BookingEventPaginated).data ?? []);
          this.eventsForToday.set(events);
        }),
        catchError(() => {
          return of();
        })
      );
  }

  getBookingEvent(params: ActivityQueryParams): Observable<void> {
    this.loading.set(true);
    this.lastDateRange.set(params);
    return this.httpApi.get<BookingEvent[]>(`${this.baseUrl}/booking-event`, { params }).pipe(
      map(res => {
        this.events.set(res);
        this.loading.set(false);
      }),
      catchError(() => {
        this.loading.set(false);
        return of();
      }),
      finalize(() => this.loading.set(false))
    );
  }

  getBookingEventById(id: string): Observable<LiveClass> {
    return this.httpApi.get<LiveClass>(`${this.baseUrl}/booking-event/${id}`).pipe(
      map(res => {
        this.loading.set(false);
        return res;
      }),
      catchError(() => {
        this.loading.set(false);
        return of();
      })
    );
  }

  acceptEvent(id: string): Observable<LiveClass> {
    this.loading.set(true);
    return this.httpApi.patch<LiveClass>(`${this.baseUrl}/booking-event/${id}/participant/accept`, {}).pipe(
      map(res => {
        this.loading.set(false);
        return res;
      }),
      catchError(() => {
        this.loading.set(false);
        return of();
      })
    );
  }

  rejectEvent(id: string): Observable<LiveClass> {
    this.loading.set(true);
    return this.httpApi.patch<LiveClass>(`${this.baseUrl}/booking-event/${id}/participant/reject`, {}).pipe(
      map(res => {
        this.loading.set(false);
        return res;
      }),
      catchError(() => {
        this.loading.set(false);
        return of();
      })
    );
  }

  getBookingEventAvailable(params: WlcmQueryParams = DEFAULT_QUERY_PARAMS): Observable<void> {
    this.loading.set(true);
    const queryParams = this.queryParams();
    if (queryParams) {
      params = { ...params, ...queryParams };
    }

    return this.httpApi
      .get<WlcmPaginatedData<BookingEvent>>(`${this.baseUrl}/booking-event/available`, { params })
      .pipe(
        map(res => {
          const { totalPages, totalItems, currPage } = res;
          const events = this.events();
          const current = currPage !== 1 ? (events ?? []) : [];

          this.events.set([...current, ...res.data]);
          this.pagination.set({
            totalPages,
            totalItems,
            currPage,
          });
          this.loading.set(false);
        }),
        catchError(() => {
          this.loading.set(false);
          return of();
        })
      );
  }

  getStreamById(id: string | number): Observable<JoinEvent> {
    return this.httpApi.get<JoinEvent>(`${this.baseUrl}/booking-event/${id}/join`).pipe(
      map(res => {
        this.joinEvent.set(res);
        return res;
      }),
      catchError(() => {
        this.loading.set(false);
        return of();
      })
    );
  }

  getStartMeetingUrl(id: string): Observable<StartMeetingResponse> {
    return this.httpApi.get<StartMeetingResponse>(`${this.baseUrl}/booking-event/${id}/start`).pipe(
      map(res => {
        this.startEvent.set(res);
        return res;
      }),
      catchError(() => {
        return of();
      })
    );
  }

  getZoomToken(id: number | string): Observable<string> {
    this.loading.set(true);
    return this.httpApi
      .get<string>(`${this.baseUrl}/booking-event/${id}/registrant-token`, {
        responseType: 'text',
      })
      .pipe(
        tap(res => {
          return res;
        }),
        catchError(() => {
          return of();
        }),
        finalize(() => this.loading.set(false))
      );
  }

  clearEvents(): void {
    this.events.set([]);
  }

  resetLoadingState(): void {
    this.loading.set(false);
  }
}
